import { FindingValidations } from '@/@types/persistUserValidations';

import configs from '../../../config';
import {
  CALCULUS,
  CARIES,
  PERIAPICAL_RADIOLUCENCY,
  RADIOGRAPHIC_BONELOSS,
  RESTORATIVE_ADJACENT_LUCENCY,
} from '../../../constants';
import { yellow500 } from '../../../theme/videa-style-tokens';
import { computeQuadrant, findRBLPointAnnotation } from '../../../utils/rbl';
import AnnotatorConstants from '../annotator.constants';
import type {
  BoundingBoxFinding,
  CornerstoneFriendlyAnnotation,
  Models,
  ToothIdentificationFinding,
  UserValidation,
} from '../types';

const { API_URL } = configs;

export const shouldTreatImageAsPA = (image: any) =>
  image?.detectedImageType === 'PERIAPICAL';

/**
 * Maps an annotation that was saved in the backend, to one that we can show in the application
 * The output is an annotation object in the same format as is generated while annotating with Cornerstone
 */
export const computeCornerstoneRoiFromSavedAnnotation = (
  annotation: any,
  color: any,
  spaceBetweenDashes = null,
  renderDashedLine = false,
): CornerstoneFriendlyAnnotation => {
  const { xMin, yMin, xMax, yMax, x, y, width, height } = annotation;
  const _xMin = xMin ?? x;
  const _yMin = yMin ?? y - height;
  const _xMax = xMax ?? x + width;
  const _yMax = yMax ?? y;

  const cornerstoneFriendlyAnnotation = {
    active: false,
    color,
    spaceBetweenDashes: spaceBetweenDashes ?? [],
    hasDashedBorder: renderDashedLine,
    // invalidated: true allows Cornerstone to update any relevant metrics, notably defining a cachedStats object and populating it
    // without this, there is an error within the library trying to access cachedStats.mean, cachedStats.stdDev, and cachedStats.area
    // FDA build means we get back the coordinates that cornerstone expects, so send them directly with no calculations
    invalidated: true,
    visible: true,
    handles: {
      start: {
        x: _xMin,
        y: _yMin,
      },
      end: {
        x: _xMax,
        y: _yMax,
      },
      textBox: {},
    },
    xMin: _xMin,
    yMin: _yMin,
    xMax: _xMax,
    yMax: _yMax,
  };

  return cornerstoneFriendlyAnnotation;
};

export const computeLineFromSavedAnnotation = (
  currentFinding: any,
  color: any,
) => {
  const { xCej: x1, yCej: y1, xAbd: x2, yAbd: y2 } = currentFinding;
  return {
    active: false,
    color,
    // invalidated: true allows Cornerstone to update any relevant metrics, notably defining a cachedStats object and populating it
    // without this, there is an error within the library trying to access cachedStats.mean, cachedStats.stdDev, and cachedStats.area
    invalidated: true,
    visible: true,
    handles: {
      start: {
        x: x1,
        y: y1,
      },
      end: {
        x: x2,
        y: y2,
      },
      textBox: {},
    },
  };
};

/**
 * Takes in a patientId, such as 2.25.2.0.1.604046507618970052178042351442854851484298706055, and returns a value of the form 2.25.2.0
 *
 * @param patientId-
 */
export const computeOrganizationIdentifier = (patientId: any) => {
  const splits = patientId.split('.');
  return splits.slice(0, 4).join('.');
};

/**
 * Determines the patient's folder path for a patient, by pulling one of the patient's image sourceFileNames
 * E.g. If the source file name is C:/Users/Mike/Desktop/3519/R1.rvg, it will return C:/Users/Mike/Desktop/3519
 *
 * @param {*} patient
 */
export const getPatientPath = (patient: any) => {
  const { images } = patient;
  const imageValues = Object.values(images) || [];
  const [firstImage = {}] = imageValues;
  // @ts-expect-error TS(2339): Property 'path' does not exist on type 'unknown'.
  const { path } = firstImage;

  const filePathUniversal = path.replace(/\\/g, '/');
  const splits = filePathUniversal.split('/');
  splits.pop();
  return splits.join('/') ? splits.join('/') : path;
};

export const isWebImageType = (fileType?: string) => {
  return Boolean(
    fileType?.includes('.png') ||
      fileType?.includes('.jpg') ||
      fileType?.includes('.jpeg'),
  );
};

export const computeImageAdjustmentKeyForDisplay = (
  image: any = {},
  isHighQualityEnabled = true,
) => {
  const { sharpened, generalized, original } = image.imageKeys || {};

  let keyForVisibleImage = '';

  if (isHighQualityEnabled && generalized?.key) {
    keyForVisibleImage = generalized.key;
  } else if (sharpened?.key) {
    // if enhancement failed or if normal quality is selected, show the sharpened adjustment as a fallback
    keyForVisibleImage = sharpened.key;
    // Fall back to generalized key if no sharpened image, and then original
  } else if (generalized?.key) {
    keyForVisibleImage = generalized?.key;
  } else if (isWebImageType(original?.key)) {
    // for some scenarios we get a png for the original - we can show those.
    keyForVisibleImage = original?.key || '';
  }

  return keyForVisibleImage;
};

export const computeImagePath = (
  image: any = {},
  isHighQualityEnabled = true,
) => {
  const { patientInfo, imageId } = image;
  const { patientId } = patientInfo;

  const imageKey = computeImageAdjustmentKeyForDisplay(
    image,
    isHighQualityEnabled,
  );
  if (imageKey && imageId) {
    return `${API_URL}/images/get-image?imageKey=${imageKey}&imageId=${imageId}`;
  }

  if (imageKey && patientId) {
    return `${API_URL}/images/get-image?imageKey=${imageKey}&patientId=${patientId}`;
  }
  return undefined;
};

export const appendSourceToFinding = (
  // @ts-expect-error TS(7006): Parameter 'finding' implicitly has an 'any' type.
  finding,
  // @ts-expect-error TS(7006): Parameter 'sourceImageId' implicitly has an 'any' ... Remove this comment to see the full error message
  sourceImageId,
  // @ts-expect-error TS(7006): Parameter 'type' implicitly has an 'any' type.
  type,
  // @ts-expect-error TS(7006): Parameter 'source' implicitly has an 'any' type.
  source,
  modelType: null | string = null,
) => ({
  ...finding,
  sourceImageId,
  type,
  source,
  modelType,
});

export const getAugmentedPathologiesForImage = (currentImage: any) => {
  const { models, userAnnotations = [] } = currentImage || {};
  const {
    caries = {},
    periapical_radiolucency = {},
    radiographic_boneloss = {},
    calculus = {},
    restorative_adjacent_lucency = {},
  } = models || {};

  const cariesForImage = caries?.findings || [];
  const prlForImage = periapical_radiolucency?.findings || [];
  const rblForImage = radiographic_boneloss?.findings || [];
  const calculusFindings = calculus.findings || [];
  const restorativeAdjacentLucencyFindings =
    restorative_adjacent_lucency?.findings || [];

  return {
    cariesForImage,
    prlForImage,
    rblForImage,
    userAnnotations,
    calculusFindings,
    restorativeAdjacentLucencyFindings,
  };
};

export const getPathologiesFromImages = (
  activeAiAnalysis: any,
  aiAnalyses: any,
  imageKeysToDisplay: any,
) => {
  if (activeAiAnalysis?.models) {
    // We have a selected image, so take only its systemFindings and annotations
    const {
      cariesForImage: cariesFindings = [],
      prlForImage: prlFindings = [],
      rblForImage: rblFindings = [],
      userAnnotations = [],
      calculusFindings = [],
      restorativeAdjacentLucencyFindings = [],
    } = getAugmentedPathologiesForImage(activeAiAnalysis);
    const flattenedRBLLines = flattenRBLLines(rblFindings);

    return {
      cariesFindings,
      prlFindings,
      rblFindings: flattenedRBLLines,
      userAnnotations,
      calculusFindings,
      restorativeAdjacentLucencyFindings,
    };
  }

  // Take the full list of systemFindings and annotations, from all images
  let allCariesFindings: any = [];
  let allUserFindings: any = [];
  let allRblFindings: any = [];
  let allPrlFindings: any = [];
  let allCalculusFindings: any = [];
  let allRestorativeAdjacentLucencyFindings: BoundingBoxFinding[] = [];
  for (let i = 0; i < imageKeysToDisplay.length; i++) {
    const currentImage =
      (aiAnalyses && aiAnalyses[imageKeysToDisplay[i]]) || {};
    const {
      cariesForImage,
      prlForImage,
      rblForImage,
      userAnnotations,
      calculusFindings,
      restorativeAdjacentLucencyFindings,
    } = getAugmentedPathologiesForImage(currentImage);
    const flattenedRBLLines = flattenRBLLines(rblForImage);
    allCariesFindings = [...allCariesFindings, ...cariesForImage];
    allUserFindings = [...allUserFindings, ...userAnnotations];
    allRblFindings = [...allRblFindings, ...flattenedRBLLines];
    allPrlFindings = [...allPrlFindings, ...prlForImage];
    allCalculusFindings = [...allCalculusFindings, ...calculusFindings];
    allRestorativeAdjacentLucencyFindings = [
      ...allRestorativeAdjacentLucencyFindings,
      ...restorativeAdjacentLucencyFindings,
    ];
  }
  return {
    cariesFindings: allCariesFindings,
    userAnnotations: allUserFindings,
    rblFindings: allRblFindings,
    prlFindings: allPrlFindings,
    calculusFindings: allCalculusFindings,
    restorativeAdjacentLucencyFindings: allRestorativeAdjacentLucencyFindings,
  };
};

export const determinePathologyTypeList = (pathologies: any, type: any) =>
  pathologies
    .filter((finding: any) => !finding.deleted && finding.type === type)
    .sort(
      (findingA: any, findingB: any) =>
        findingA.toothNumber - findingB.toothNumber,
    );

export const flattenRBLLines = (rblFindings: any) => {
  let flattenedRblFindingsForImage: any = [];

  for (let i = 0; i < rblFindings?.length; i++) {
    let rblLinesPerSide = rblFindings[i].sides;
    // Maintain keys of which image each finding came from
    rblLinesPerSide = rblLinesPerSide.map((f: any) => ({
      ...f,
      toothNumber: rblFindings[i].toothNumber,
    }));

    flattenedRblFindingsForImage = [
      ...flattenedRblFindingsForImage,
      ...rblLinesPerSide,
    ];
  }
  return flattenedRblFindingsForImage;
};

export const groupPathologiesByToothNumber = (findings: any) => {
  return findings.reduce(
    (toothNumberToFindingsMap: any, currentFinding: any) => {
      let { toothNumber = 0 } = currentFinding;

      if (toothNumber === null) {
        toothNumber = 0;
      }

      if (toothNumberToFindingsMap[toothNumber]) {
        toothNumberToFindingsMap[toothNumber].push(currentFinding);
      } else {
        toothNumberToFindingsMap[toothNumber] = [currentFinding];
      }

      return toothNumberToFindingsMap;
    },
    {},
  );
};

export const determineColorForFinding = (
  finding: any,
  colors: any,
  viewerMode: any,
) => {
  const { hover, validation } = finding;
  if (viewerMode !== AnnotatorConstants.MODES.MAGNIFYING && hover) {
    // hover has highest priority
    return colors.hover;
  }
  if (validation) {
    if (
      finding.type === RESTORATIVE_ADJACENT_LUCENCY &&
      validation === 'Agree'
    ) {
      return colors.red;
    }
    // next is validation
    return colors[validation];
  }
  if (finding.type === CALCULUS) {
    return colors.opaqueYellowOrange;
  }
  if (finding.type === RESTORATIVE_ADJACENT_LUCENCY) {
    return colors.yellow;
  } //h
  if (finding?.depth?.trim() === 'into_enamel') {
    return yellow500;
  }
  return colors.cariesHighConfidence;
};

export const determineClassForSegmentation = (
  findingType: keyof Models,
  validation?: UserValidation,
) => {
  let className = '';

  switch (findingType) {
    case 'caries':
      className = CARIES;
      break;
    case 'periapical_radiolucency':
      className = PERIAPICAL_RADIOLUCENCY;
      break;
    case 'restorative_adjacent_lucency':
      className = RESTORATIVE_ADJACENT_LUCENCY;
      break;
    case 'calculus':
      className = CALCULUS;
      break;
    default:
      break;
  }

  if (validation === FindingValidations.Monitor) {
    className += ' monitored';
  }

  return className;
};

export const determineClassForFinding = (finding: any, viewerMode: any) => {
  const { hover, validation } = finding;
  const findingTypeKebab = finding.type
    ?.replace(/([a-z])([A-Z])/g, '$1-$2')
    ?.replace(/\s+/g, '-')
    ?.toLowerCase();
  const classes = [findingTypeKebab];

  if (viewerMode !== AnnotatorConstants.MODES.MAGNIFYING && hover) {
    // hover has highest priority
    classes.push('hovered');
  }
  if (validation && validation === 'Agree') {
    classes.push('validated');
  }
  if (validation && validation === 'Monitor') {
    classes.push('monitored');
  }
  if (finding?.depth?.trim() === 'into_enamel') {
    classes.push('enamel-only-caries');
  }
  return classes.join(' ');
};

export const getTopLeftBottomRightCoordinates = (handles: any) => {
  const {
    start: { x: startX, y: startY },
  } = handles;
  const {
    end: { x: endX, y: endY },
  } = handles;

  const leftX = startX < endX ? startX : endX;
  const rightX = startX > endX ? startX : endX;

  const topY = startY < endY ? startY : endY;
  const bottomY = startY > endY ? startY : endY;

  return { leftX, rightX, topY, bottomY };
};

export const getRblPointHoverRange = (handles: any) => {
  // For RBL points, we anticipate the x,y values in the `handles.start` and `handles.end` to be the same.
  const OFFSET = 20;
  const {
    start: { x, y },
  } = handles;

  const leftX = x - OFFSET;
  const rightX = x + OFFSET;

  const topY = y - OFFSET;
  const bottomY = y + OFFSET;

  return { leftX, rightX, topY, bottomY };
};

/**
 * Takes in an annotation list, and sees if the point hovered on the image matches any of the annotations
 * This is done by putting all pathologies and userAnnotations into the same cornerstone-friendly format
 * Then, findMatchingAnnotation treats the point as a user finding, which is cornerstone-friendly, and sees if it matches any
 *
 * @param {*} pathologies
 * @param {*} userAnnotations
 * @param {*} point
 */
export const findHoveredAnnotation = (
  borderedPathologies: any = [],
  userAnnotations: any = [],
  point: any,
) => {
  const systemFormattedAnnotation = {
    ...point,
    width: 0,
    height: 0,
  };

  const cornerstoneFriendlyBoxPathologies = borderedPathologies
    // makes sure findings that were disagreed are not shown
    // @ts-expect-error TS(2339): Property 'validation' does not exist on type 'neve... Remove this comment to see the full error message
    .filter((path) => path.validation !== 'Disagree')
    .map((path: any) => ({
      ...computeCornerstoneRoiFromSavedAnnotation(path, ''),
      id: path.id,
    }));
  const sourceAnnotationList = [
    ...cornerstoneFriendlyBoxPathologies,
    ...userAnnotations,
  ];
  const matchingAnnotation = findMatchingAnnotation(
    sourceAnnotationList,
    systemFormattedAnnotation,
  );

  return matchingAnnotation
    ? borderedPathologies.find(
        (f: any) =>
          f.id === matchingAnnotation.id ||
          f.id === matchingAnnotation.objectId,
      )
    : null;
};

export const findHoveredRblPointAnnotation = (point: any, rblFindings = []) => {
  const systemFormattedAnnotation = {
    ...point,
    width: 0,
    height: 0,
  };
  const flattenedRBLLines = flattenRBLLines(rblFindings);
  const cornerstoneFriendlyRblPoints = flattenedRBLLines
    .filter(
      // @ts-expect-error TS(7006): Parameter 'path' implicitly has an 'any' type.
      (path) =>
        path.validation !== 'Disagree' &&
        path.points.find((c: any) => c.rblType === 'abd') &&
        path.points.find((c: any) => c.rblType === 'cej'),
    )
    // @ts-expect-error TS(7006): Parameter 'cornerstoneFriendlyRbl' implicitly has ... Remove this comment to see the full error message
    .reduce((cornerstoneFriendlyRbl, curr) => {
      curr?.points?.forEach((rblPoint: any) => {
        const { x, y, id } = rblPoint;
        const pointAnnotation = { xCej: x, yCej: y, xAbd: x, yAbd: y };
        const annotation = {
          ...computeLineFromSavedAnnotation(pointAnnotation, ''),
          id,
          type: 'point',
        };
        cornerstoneFriendlyRbl.push(annotation);
      });
      return cornerstoneFriendlyRbl;
    }, []);

  const matchingAnnotation = findMatchingAnnotation(
    cornerstoneFriendlyRblPoints,
    systemFormattedAnnotation,
  );
  return matchingAnnotation
    ? findRBLPointAnnotation(matchingAnnotation.id, rblFindings)
    : null;
};

/**
 * Takes in an annotation list, and sees if there is overlap with the inputted annotation
 *
 * @param sourceAnnotationList - { array }
 * @param annotation - { object }
 */
export const findMatchingAnnotation = (
  sourceAnnotationList: any,
  annotation: any,
) =>
  sourceAnnotationList.find((a: any) => {
    let cornerstoneFriendlySystemAnnotation = null;
    let cornerstoneFriendlyUserAnnotation = null;

    if (a && a.handles) {
      // We are looking for a matching user annotation, from a particular system annotation
      // The blue color is not necessary but is just a placeholder
      cornerstoneFriendlySystemAnnotation =
        computeCornerstoneRoiFromSavedAnnotation(annotation, 'blue');
      cornerstoneFriendlyUserAnnotation = a;
    } else {
      // We are looking for a matching system annotation, from a particular user annotation
      cornerstoneFriendlySystemAnnotation = a;
      cornerstoneFriendlyUserAnnotation = annotation;
    }

    const userCoordinates =
      cornerstoneFriendlyUserAnnotation.type === 'point'
        ? getRblPointHoverRange(cornerstoneFriendlyUserAnnotation.handles)
        : getTopLeftBottomRightCoordinates(
            cornerstoneFriendlyUserAnnotation.handles,
          );
    const systemCoordinates = getTopLeftBottomRightCoordinates(
      cornerstoneFriendlySystemAnnotation.handles,
    );

    const {
      leftX: leftUserX,
      rightX: rightUserX,
      topY: topUserY,
      bottomY: bottomUserY,
    } = userCoordinates;
    const {
      leftX: leftSystemX,
      rightX: rightSystemX,
      topY: topSystemY,
      bottomY: bottomSystemY,
    } = systemCoordinates;
    const hasMatches =
      leftUserX <= rightSystemX &&
      leftSystemX <= rightUserX &&
      topUserY <= bottomSystemY &&
      topSystemY <= bottomUserY;

    return hasMatches;
  });

/**
 * Takes in a list of images, and determines those that should be hidden (such as retakes, or PAs if the BW filter is selected)
 * (currently only if we got the metadata from an XML, and the image is not in the XML, or it is marked as retaken)
 *
 * @param {*} images
 * @param {*} currentImageFilter
 */
export const applyImageFilters = (
  images: any,
  currentImageFilter: any,
  selectedTeeth: string[],
  selectedQuadrants: number[],
) => {
  const isOtherFilter = currentImageFilter === AnnotatorConstants.OTHER;
  if (isOtherFilter) {
    return images?.filter((image: any) => image.imageGroup === 'OTHER');
  }

  const filtered = images?.filter((image: any) => {
    if (currentImageFilter === AnnotatorConstants.BITEWING) {
      return AnnotatorConstants.BW_ROW_INDEXES.includes(image.fmxIndex);
    }

    if (currentImageFilter === AnnotatorConstants.ODONTOGRAM) {
      const imageTeeth =
        image.models?.tooth_identification?.findings.map(
          (toothId: ToothIdentificationFinding) => toothId.toothNumber,
        ) ?? [];

      const imageQuadrants =
        image.models?.tooth_identification?.findings.map(
          (toothId: ToothIdentificationFinding) =>
            computeQuadrant(toothId.toothNumber),
        ) ?? [];

      return (
        imageTeeth.some((tooth: string) => selectedTeeth.includes(tooth)) ||
        imageQuadrants.some((quadrant: number) =>
          selectedQuadrants.includes(quadrant),
        )
      );
    }

    return true;
  });

  return filtered;
};

export const getPatientIdFromS3Path = (s3Path = '') => {
  const s3PathElements = s3Path.split('/');
  const s3ElementsWithoutFileName = s3PathElements.slice(0, -1);

  return s3ElementsWithoutFileName.join('.');
};

export const augmentPathologiesOnImage = (
  currentImage: any,
  sourceImageId: any,
  userAnnotationsFromState = [],
) => {
  const { models, userAnnotations = [] } = currentImage || {};
  const {
    caries = {},
    periapical_radiolucency = {},
    radiographic_boneloss = {},
    calculus = {},
    restorative_adjacent_lucency = {},
  } = models || {};
  const cariesForImage = (caries?.findings || []).map((f: any) =>
    appendSourceToFinding(f, sourceImageId, CARIES, 'AI', caries.type),
  );
  const prlForImage = (periapical_radiolucency?.findings || []).map((f: any) =>
    appendSourceToFinding(
      f,
      sourceImageId,
      PERIAPICAL_RADIOLUCENCY,
      'AI',
      periapical_radiolucency.type,
    ),
  );
  let augmentedUserAnnotations = userAnnotations?.map((f: any) =>
    appendSourceToFinding(f, sourceImageId, f.type, 'userAnnotations'),
  );
  // images received in the latest-images response would not have any userAnnotations. To persist userAnnotations,
  // grab them from the existing state and append them to the corresponding image in the latest-images response
  if (!augmentedUserAnnotations?.length && userAnnotationsFromState?.length) {
    augmentedUserAnnotations = userAnnotationsFromState;
  }

  const rblForImage = radiographic_boneloss?.findings?.map((f: any) => ({
    ...f,

    sides: f.sides.map((side: any) =>
      appendSourceToFinding(side, sourceImageId, RADIOGRAPHIC_BONELOSS, 'AI'),
    ),
  }));

  const calculusForImage = calculus?.findings?.map((f: any) =>
    appendSourceToFinding(f, sourceImageId, CALCULUS, 'AI', calculus.type),
  );

  const restorativeAdjacentLucencyFindings =
    restorative_adjacent_lucency?.findings?.map((f: BoundingBoxFinding) =>
      appendSourceToFinding(
        f,
        sourceImageId,
        RESTORATIVE_ADJACENT_LUCENCY,
        'AI',
      ),
    );

  const result = {
    ...currentImage,
    models: {
      ...models,
      caries: {
        ...caries,
        findings: cariesForImage,
      },
      periapical_radiolucency: {
        ...periapical_radiolucency,
        findings: prlForImage,
      },
      radiographic_boneloss: {
        ...radiographic_boneloss,
        findings: rblForImage,
      },
      calculus: {
        ...calculus,
        findings: calculusForImage,
      },
      restorative_adjacent_lucency: {
        ...restorative_adjacent_lucency,
        findings: restorativeAdjacentLucencyFindings,
      },
    },
    userAnnotations: augmentedUserAnnotations,
  };

  return result;
};

export const isAiEnabledForFindingType = (
  activeImage: any,
  findingType: any,
) => {
  const models = activeImage?.models ?? {};
  if (
    models[findingType]?.modelState === 'NOT_REQUESTED' ||
    models[findingType]?.modelState === 'SKIPPED'
  ) {
    return false;
  }
  // for older analyses we used to return null for the model type if the model was not requested during analysis
  return Boolean(models[findingType]?.modelId);
};

export const computeCursorClass = (viewerModeParam: any) => {
  switch (viewerModeParam) {
    case AnnotatorConstants.MODES.VIEWING:
      return 'default';
    case AnnotatorConstants.MODES.MAGNIFYING:
      return 'magnifyMode';
    case AnnotatorConstants.MODES.DRAWING:
      return 'drawMode';
    default:
      return '';
  }
};
